import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Spin, message } from 'antd';
import { gql, useMutation, useQuery, useLazyQuery } from '@apollo/client';

import { ManageCategory } from '../../components/ManageCategory';

import {
  DESIGNATIONS_OPTION_QUERY,
  LOCATIONS_OPTIONS_QUERY,
  PORTFOLIO_OPTIONS_QUERY,
  REGIONS_OPTION_QUERY,
  CATEGORIES_GET_QUERY,
  LEAVE_TYPE_OPTIONS_QUERY,
  ALLOWANCE_TYPE_OPTIONS_QUERY,
  DEDUCTION_TYPE_OPTIONS_QUERY,
  PRODUCT_TYPE_OPTIONS_QUERY,
  STAGE_OPTIONS_QUERY,
  CONVEYANCE_TYPE_OPTIONS_QUERY,
} from '../../apollo/queries';

const CATEGORY_ADD_QUERY = gql`
  mutation AddCategory($input: AddCategoryInput) {
    addCategory(input: $input) {
      ... on Category {
        id
        name
        code
        status
        order
      }
    }
  }
`;

const CATEGORY_UPDATE_QUERY = gql`
  mutation UpdateCategory($input: UpdateCategoryInput) {
    updateCategory(input: $input) {
      ... on Category {
        id
        name
        code
        status
        order
      }
    }
  }
`;

const CATEGORY_UPDATE_ORDER_QUERY = gql`
  mutation UpdateCategoryOrder($input: UpdateCategoryOrderInput) {
    updateCategoryOrder(input: $input) {
      ... on Category {
        id
        name
        code
        status
        order
      }
    }
  }
`;

const CategoryManager = ({ catType, havingReference, reference }) => {
  const { user } = useSelector((state) => state.login);
  const [loadDesignations] = useLazyQuery(DESIGNATIONS_OPTION_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [LoadPortfolios] = useLazyQuery(PORTFOLIO_OPTIONS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [loadRegions] = useLazyQuery(REGIONS_OPTION_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [loadLocations] = useLazyQuery(LOCATIONS_OPTIONS_QUERY, {
    variables: {
      userId: user.id,
    },
    fetchPolicy: 'network-only',
  });
  const [LoadLeaveTypes] = useLazyQuery(LEAVE_TYPE_OPTIONS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [LoadAllowanceTypes] = useLazyQuery(ALLOWANCE_TYPE_OPTIONS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [LoadDeductionTypes] = useLazyQuery(DEDUCTION_TYPE_OPTIONS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [LoadProductTypes] = useLazyQuery(PRODUCT_TYPE_OPTIONS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [LoadStages] = useLazyQuery(STAGE_OPTIONS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [LoadConveyanceTypes] = useLazyQuery(CONVEYANCE_TYPE_OPTIONS_QUERY, {
    fetchPolicy: 'network-only',
  });

  const onSuccessMutation = (
    data,
    mesageText = 'Details updated successfully!'
  ) => {
    refetch();
    message.success(mesageText);
    //refetch read list
    if (data.__typename === 'Designation') {
      loadDesignations();
    } else if (data.__typename === 'Portfolio') {
      LoadPortfolios();
    } else if (data.__typename === 'Region') {
      loadRegions();
    } else if (data.__typename === 'Location') {
      loadLocations();
    } else if (data.__typename === 'LeaveType') {
      LoadLeaveTypes();
    } else if (data.__typename === 'AllowanceType') {
      LoadAllowanceTypes();
    } else if (data.__typename === 'DeductionType') {
      LoadDeductionTypes();
    } else if (data.__typename === 'ProductType') {
      LoadProductTypes();
    } else if (data.__typename === 'Stage') {
      LoadStages();
    } else if (data.__typename === 'ConveyanceType') {
      LoadConveyanceTypes();
    }
  };

  const { loading, data, refetch } = useQuery(CATEGORIES_GET_QUERY, {
    variables: {
      type: catType,
    },
  });

  const [getReference, { data: refData }] = useLazyQuery(CATEGORIES_GET_QUERY);

  useEffect(() => {
    if (havingReference) {
      getReference({
        variables: {
          type: reference.type,
        },
      });
    }
  }, [havingReference, getReference, reference]);

  const [addCategory, { loading: doingAdd }] = useMutation(CATEGORY_ADD_QUERY);
  const [updateCategory, { loading: doingUpdate }] = useMutation(
    CATEGORY_UPDATE_QUERY
  );
  const [updateCategoryOrder, { loading: doingUpdateOrder }] = useMutation(
    CATEGORY_UPDATE_ORDER_QUERY
  );

  const onSave = (values, callBack) => {
    console.log('onSave:Category called with ///', values);
    updateCategory({
      variables: {
        input: {
          type: catType,
          id: parseInt(values.id),
          name: values.name,
          code: values.code,
          status: values.status,
          reference_id:
            havingReference && !isNaN(parseInt(values[reference.key].name))
              ? parseInt(values[reference.key].name)
              : null,
        },
      },
    })
      .then(({ data: { updateCategory } }) => {
        console.log('onSave:Category done', updateCategory);
        if (updateCategory) {
          callBack(true);
          onSuccessMutation(updateCategory);
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onSaveOrder = (values, callBack) => {
    console.log('onSaveOrder:Category called with ///', values);
    updateCategoryOrder({
      variables: {
        input: {
          type: catType,
          idsInOrder: values.map((i) => parseInt(i)),
        },
      },
    })
      .then(({ data: { updateCategoryOrder } }) => {
        console.log('onSave:Category done', updateCategoryOrder);
        if (updateCategoryOrder) {
          callBack(true);
          onSuccessMutation(updateCategoryOrder, 'Order updated!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onSaveAddNewCategory = (values, callBack) => {
    console.log('onSaveAddNewCategory called with ///', values);
    addCategory({
      variables: {
        input: {
          type: catType,
          name: values.name,
          code: values.code,
          status: values.status === true ? 1 : 0,
          reference_id: !isNaN(parseInt(values.reference))
            ? parseInt(values.reference)
            : null,
        },
      },
    })
      .then(({ data: { addCategory } }) => {
        console.log('onAddSave:Category done', addCategory);
        if (addCategory) {
          callBack(true);
          onSuccessMutation(addCategory);
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  let columns = [
    {
      title: 'Enabled',
      dataIndex: 'status',
      key: 'status',
      width: '90px',
      align: 'center',
      editable: true,
      className: 'drag-visible',
      render: (_, record) => {
        return record.status === 1 ? 'Yes' : 'No';
      },
    },
    {
      title: 'Title',
      dataIndex: 'name',
      key: 'name',
      editable: true,
      className: 'drag-visible',
    },
    {
      title: havingReference ? reference.title : 'Reference',
      dataIndex: havingReference ? reference.dataIndex : 'reference_id',
      key: havingReference ? reference.key : 'reference_id',
      width: '200px',
      editable: true,
      typeReference: true,
      className: 'drag-visible',
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: '100px',
      editable: true,
      className: 'drag-visible',
    },
  ];

  columns = columns.filter(
    (column) => column.key !== 'reference_id' || havingReference
  );

  return (
    <Spin spinning={loading || doingAdd || doingUpdate || doingUpdateOrder}>
      {/* <Card style={{ minHeight: '200px', borderTop: '0' }}> */}
      {data && data.categories && (
        <ManageCategory
          dataSource={data.categories}
          onSave={onSave}
          onSaveOrder={onSaveOrder}
          onSaveAddNewCategory={onSaveAddNewCategory}
          columns={columns}
          havingReference={havingReference}
          reference={reference}
          refData={refData && refData.categories}
        />
      )}
      {/* </Card> */}
    </Spin>
  );
};

export default CategoryManager;
